// src/components/richtext/RichTextEditorTipTap.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'
import Typography from '@tiptap/extension-typography'
import { sanitizeHtml } from '../../lib/richtext/htmlSanitizer'
import { toPlainTextFromHtml, ensureTipTapFormat } from '../../lib/richtext/tiptapConverters'
import '../../styles/tiptap.css'
import Tooltip from '../common/Tooltip'
import LinkModal from './LinkModal'

export default function RichTextEditorTipTap({
  value,            // { content_json?, content_html?, content_plain? }
  onChange,         // ({ content_json, content_html, content_plain })
  placeholder = 'Write something…',
  readOnly = false,
  error = false,
  disabled = false,
  className = ''
}) {
  
  const editorRef = useRef(null)
  const isUpdatingContent = useRef(false)

  // Link modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkModalInitialUrl, setLinkModalInitialUrl] = useState('')

  // TipTap extensions configuration
  const extensions = useMemo(() => [
    StarterKit.configure({
      bulletList: { keepMarks: true },
      orderedList: { keepMarks: true },
      heading: { levels: [1, 2, 3] },
      link: false, // Disable default link extension
      underline: false // Disable default underline extension
    }),
    Link.configure({ 
      openOnClick: false,  // Don't open links in edit mode
      autolink: true,      // Auto-detect and linkify URLs
      linkOnPaste: true,   // Convert pasted URLs to links
      HTMLAttributes: {
        rel: 'noopener noreferrer nofollow',
        target: '_blank'
      }
    }),
    Underline,
    Typography,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] })
  ], [])

  // Initialize TipTap editor
  const editor = useEditor({
    extensions,
    editable: !readOnly && !disabled,
    content: (() => {
      const normalized = ensureTipTapFormat(value)
      // If we have JSON content, use it; otherwise use HTML content
      return normalized.content_json || normalized.content_html || ''
    })(),
    onUpdate: ({ editor }) => {
      // Skip onChange if we're currently updating content programmatically
      if (isUpdatingContent.current) {
        return;
      }
      
      if (onChange) {
        const json = editor.getJSON()
        const html = sanitizeHtml(editor.getHTML())
        const text = toPlainTextFromHtml(html)
        try {
          onChange({ content_json: json, content_html: html, content_plain: text })
        } catch (error) {
          console.error('Error calling onChange:', error);
        }
      }
    },
    editorProps: {
      attributes: { 
        'aria-label': 'Rich text editor', 
        class: 'tiptap-editor',
        'data-placeholder': placeholder
      }
    }
  })

  // Handle external value updates (e.g., switching notes)
  useEffect(() => {
    if (!editor) return
    
    const normalizedValue = ensureTipTapFormat(value)
    
    // Determine what content to use
    const newContent = normalizedValue.content_json || normalizedValue.content_html || ''
    
    // Only update if the content is actually different
    const currentJson = editor.getJSON()
    const currentHtml = editor.getHTML()
    
    // Compare both JSON and HTML to see if content has changed
    const jsonChanged = normalizedValue.content_json && 
      JSON.stringify(currentJson) !== JSON.stringify(normalizedValue.content_json)
    const htmlChanged = normalizedValue.content_html && 
      currentHtml !== normalizedValue.content_html
    
    if (jsonChanged || htmlChanged) {
      // Safari workaround: Use a small delay to prevent race conditions
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari && newContent && newContent !== '<p></p>') {
        isUpdatingContent.current = true;
        setTimeout(() => {
          editor.commands.setContent(newContent, false);
          setTimeout(() => {
            isUpdatingContent.current = false;
          }, 100);
        }, 50);
      } else {
        isUpdatingContent.current = true;
        editor.commands.setContent(newContent, false);
        setTimeout(() => {
          isUpdatingContent.current = false;
        }, 100);
      }
    }
  }, [value, editor])

  // Handle readOnly/disabled state changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && !disabled)
    }
  }, [readOnly, disabled, editor])

  // Auto-resize functionality
  useEffect(() => {
    if (!editor || !editorRef.current) return

    const autoResize = () => {
      const editorElement = editorRef.current?.querySelector('.ProseMirror')
      if (!editorElement) return

      // Reset height to auto to get the natural height
      editorElement.style.height = 'auto'
      
      // Get the scroll height and set it as the new height
      const scrollHeight = editorElement.scrollHeight
      const minHeight = 120
      const maxHeight = window.innerWidth <= 640 ? 300 : window.innerWidth <= 1024 ? 350 : 500
      
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
      editorElement.style.height = `${newHeight}px`
    }

    // Auto-resize on content change
    editor.on('update', autoResize)
    editor.on('create', autoResize)

    // Initial resize
    setTimeout(autoResize, 100)

    // Cleanup
    return () => {
      editor.off('update', autoResize)
      editor.off('create', autoResize)
    }
  }, [editor])

  if (!editor) return null

  return (
    <div className={`rte-tt-container ${error ? 'error' : ''} ${className}`} ref={editorRef}>
      {!readOnly && !disabled && (
        <div className="rte-tt-toolbar" role="toolbar" aria-label="Formatting toolbar">

          {/* Text formatting */}
          <Tooltip content="Bold (Ctrl+B)">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleBold().run()} 
            aria-pressed={editor.isActive('bold')} 
            className={`rte-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
          >
            B
          </button>
          </Tooltip>
          
          <Tooltip content="Italic (Ctrl+I)">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleItalic().run()} 
            aria-pressed={editor.isActive('italic')} 
            className={`rte-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
          >
            I
          </button>
          </Tooltip>
          
          <Tooltip content="Underline (Ctrl+U)">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            aria-pressed={editor.isActive('underline')}
            className={`rte-btn ${editor.isActive('underline') ? 'is-active' : ''}`}
          >
            U
          </button>
          </Tooltip>

          <Tooltip content="Strikethrough">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            aria-pressed={editor.isActive('strike')}
            className={`rte-btn ${editor.isActive('strike') ? 'is-active' : ''}`}
            style={{ textDecoration: 'line-through' }}
          >
            S
          </button>
          </Tooltip>

          <span className="rte-sep" />
          
          {/* Lists */}
          <Tooltip content="Bullet List">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleBulletList().run()} 
            aria-pressed={editor.isActive('bulletList')} 
            className={`rte-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          >
            • List
          </button>
          </Tooltip>
          
          <Tooltip content="Numbered List">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleOrderedList().run()} 
            aria-pressed={editor.isActive('orderedList')} 
            className={`rte-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          >
            1. List
          </button>
          </Tooltip>
          
          <span className="rte-sep" />
          
          {/* Headings */}
          <Tooltip content="Heading 1">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} 
            aria-pressed={editor.isActive('heading', { level: 1 })} 
            className={`rte-btn ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
          >
            H1
          </button>
          </Tooltip>
          
          <Tooltip content="Heading 2">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} 
            aria-pressed={editor.isActive('heading', { level: 2 })} 
            className={`rte-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
          >
            H2
          </button>
          </Tooltip>
          
          <Tooltip content="Heading 3">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            aria-pressed={editor.isActive('heading', { level: 3 })}
            className={`rte-btn ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
          >
            H3
          </button>
          </Tooltip>

          <span className="rte-sep" />

          {/* Text Alignment */}
          <Tooltip content="Align Left">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            aria-pressed={editor.isActive({ textAlign: 'left' })}
            className={`rte-btn ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
          >
            ≡←
          </button>
          </Tooltip>

          <Tooltip content="Align Center">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            aria-pressed={editor.isActive({ textAlign: 'center' })}
            className={`rte-btn ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
          >
            ≡
          </button>
          </Tooltip>

          <Tooltip content="Align Right">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            aria-pressed={editor.isActive({ textAlign: 'right' })}
            className={`rte-btn ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
          >
            →≡
          </button>
          </Tooltip>

          <Tooltip content="Justify">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            aria-pressed={editor.isActive({ textAlign: 'justify' })}
            className={`rte-btn ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`}
          >
            ≡≡
          </button>
          </Tooltip>

          <span className="rte-sep" />

          {/* Link */}
          <Tooltip content={editor.isActive('link') ? 'Edit Link' : 'Add Link'}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setLinkModalInitialUrl(editor.getAttributes('link').href || '');
              setLinkModalOpen(true);
            }}
            aria-pressed={editor.isActive('link')}
            className={`rte-btn ${editor.isActive('link') ? 'is-active' : ''}`}
          >
            🔗
          </button>
          </Tooltip>
          
          <span className="rte-sep" />
          
          {/* Colors */}
          <Tooltip content="Text Color">
          <label className="rte-color">
            <span className="rte-label">A</span>
            <input
              type="color"
              aria-label="Text color"
              defaultValue="#000000"
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
          </label>
          </Tooltip>

          <Tooltip content="Highlight Color">
          <label className="rte-color">
            <span className="rte-label bg">A</span>
            <input
              type="color"
              aria-label="Highlight color"
              defaultValue="#ffff00"
              onChange={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()}
            />
          </label>
          </Tooltip>

          
          
          <span className="rte-sep" />
          
          {/* Block elements */}
          <Tooltip content="Quote">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().toggleBlockquote().run()} 
            aria-pressed={editor.isActive('blockquote')} 
            className={`rte-btn ${editor.isActive('blockquote') ? 'is-active' : ''}`}
          >
            ❝
          </button>
          </Tooltip>
          
          <Tooltip content="Code Block">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            aria-pressed={editor.isActive('codeBlock')}
            className={`rte-btn ${editor.isActive('codeBlock') ? 'is-active' : ''}`}
          >
            {'{ }'}
          </button>
          </Tooltip>

          <Tooltip content="Horizontal Rule">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="rte-btn"
          >
            ―
          </button>
          </Tooltip>

          <span className="rte-sep" />

          <Tooltip content="Clear Formatting">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            className="rte-btn"
          >
            ✕
          </button>
          </Tooltip>

          <span className="rte-grow" />
          
          {/* Undo/Redo */}
          <Tooltip content="Undo (Ctrl+Z)">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().undo().run()} 
            disabled={!editor.can().undo()}
            className="rte-btn"
          >
            Undo
          </button>
          </Tooltip>
          
          <Tooltip content="Redo (Ctrl+Y)">
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => editor.chain().focus().redo().run()} 
            disabled={!editor.can().redo()}
            className="rte-btn"
          >
            Redo
          </button>
          </Tooltip>
        </div>
      )}
      
      <EditorContent editor={editor} />

      <LinkModal
        isOpen={linkModalOpen}
        onClose={() => {
          setLinkModalOpen(false);
          editor.chain().focus().run();
        }}
        onSubmit={(url) => {
          editor.chain().focus().setLink({ href: url }).run();
        }}
        onRemove={() => {
          editor.chain().focus().unsetLink().run();
        }}
        initialUrl={linkModalInitialUrl}
      />
    </div>
  )
}
